/**
 * Mentor IHTP — chat sobre a filosofia IHTP (livros, palestras, audiobooks),
 * com respostas geradas apenas a partir das fontes oficiais.
 */
import { useEffect, useRef, useState } from "react";
import { Send, Loader2, BookOpen, Trash2, Plus, MessageCircle } from "lucide-react";
import { myApi } from "../../api";

export default function MentorChat() {
  const [conversas, setConversas] = useState([]);
  const [conversaId, setConversaId] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { myApi.mentorConversas().then(setConversas).catch(() => {}); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, sending]);

  const openConversa = async (id) => {
    setConversaId(id);
    if (!id) { setMsgs([]); return; }
    try {
      const d = await myApi.mentorConversa(id);
      setMsgs(d.mensagens);
    } catch { setMsgs([]); }
  };

  const removeConversa = async (id) => {
    if (!window.confirm("Apagar esta conversa?")) return;
    try {
      await myApi.mentorConversaDelete(id);
      setConversas((c) => c.filter((x) => x.id !== id));
      if (conversaId === id) { setConversaId(null); setMsgs([]); }
    } catch {}
  };

  const askMentor = async (payload) => {
    try {
      return await myApi.mentorChat(payload);
    } catch (e) {
      if (e?.status === 400 || e?.status === 401 || e?.status === 404) throw e;
      await new Promise((r) => setTimeout(r, 2500));
      return await myApi.mentorChat(payload);
    }
  };

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", text }]);
    setSending(true);
    try {
      const r = await askMentor({ conversa_id: conversaId, message: text });
      setMsgs((m) => [...m, { role: "assistant", text: r.reply, fontes: r.fontes }]);
      if (!conversaId) {
        setConversaId(r.conversa_id);
        myApi.mentorConversas().then(setConversas).catch(() => {});
      }
    } catch (e) {
      setMsgs((m) => [...m, { role: "assistant", text: e?.message || "O Mentor está indisponível. Tenta novamente.", erro: true }]);
    } finally {
      setSending(false);
    }
  };

  const SUGESTOES = [
    "Como posso activar o meu poder interior?",
    "O que diz a filosofia IHTP sobre lidar com o medo de falhar?",
    "Como definir objetivos que realmente se concretizam?",
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-4" data-testid="mentor-chat">
      {/* Conversas */}
      <div className="bg-white rounded-2xl border border-[#E5E7EB] p-3 h-fit">
        <button onClick={() => openConversa(null)} data-testid="mentor-new-chat"
          className="w-full inline-flex items-center justify-center gap-1.5 text-sm bg-[#0F1829] text-white rounded-md py-2 mb-2 hover:opacity-90">
          <Plus size={15} /> Nova conversa
        </button>
        <div className="space-y-1 max-h-[320px] overflow-y-auto">
          {conversas.map((c) => (
            <div key={c.id} className={`group flex items-center gap-1 rounded-md px-2 py-1.5 text-xs cursor-pointer ${conversaId === c.id ? "bg-[#F3F4F6] font-medium" : "hover:bg-[#F9FAFB]"}`}
              onClick={() => openConversa(c.id)} data-testid={`mentor-conversa-${c.id}`}>
              <MessageCircle size={13} className="text-[#978D78] shrink-0" />
              <span className="flex-1 truncate text-[#374151]">{c.titulo || "Conversa"}</span>
              <button onClick={(e) => { e.stopPropagation(); removeConversa(c.id); }}
                className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 shrink-0">
                <Trash2 size={12} />
              </button>
            </div>
          ))}
          {conversas.length === 0 && <p className="text-xs text-[#9CA3AF] px-2 py-1">Sem conversas ainda.</p>}
        </div>
      </div>

      {/* Chat */}
      <div className="bg-white rounded-2xl border border-[#E5E7EB] flex flex-col min-h-[480px]">
        <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-4 max-h-[55vh]">
          {msgs.length === 0 && (
            <div className="text-center py-8">
              <div className="w-14 h-14 mx-auto rounded-full bg-[#0F1829] text-white grid place-items-center mb-3">
                <BookOpen size={24} />
              </div>
              <h3 className="font-serif text-lg text-[#0F1829] mb-1">Mentor IHTP</h3>
              <p className="text-sm text-[#6B7280] max-w-md mx-auto mb-4">
                Faz perguntas sobre a filosofia IHTP — respondo com base nos livros,
                palestras e audiobooks do Dr. Adelino Cunha.
              </p>
              <div className="flex flex-wrap justify-center gap-2">
                {SUGESTOES.map((s) => (
                  <button key={s} onClick={() => setInput(s)}
                    className="text-xs px-3 py-1.5 rounded-full border border-[#E5E7EB] text-[#374151] hover:bg-[#F3F4F6]"
                    data-testid="mentor-sugestao">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {msgs.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                m.role === "user" ? "bg-[#0F1829] text-white rounded-br-md"
                : m.erro ? "bg-red-50 text-red-700 border border-red-100"
                : "bg-[#F5F2EB] text-[#1F2937] rounded-bl-md"}`}>
                {m.text}
                {m.fontes?.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-[#E5E7EB]/60 text-[11px] text-[#978D78]">
                    Fontes: {m.fontes.join(" · ")}
                  </div>
                )}
              </div>
            </div>
          ))}
          {sending && (
            <div className="flex items-center gap-2 text-sm text-[#978D78]">
              <Loader2 size={15} className="animate-spin" /> O Mentor está a reflectir...
            </div>
          )}
          <div ref={endRef} />
        </div>
        <div className="border-t border-[#F3F4F6] p-3 flex gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="Pergunta ao Mentor IHTP..."
            data-testid="mentor-input"
            className="flex-1 border border-[#E5E7EB] rounded-full px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0F1829]/20"
          />
          <button onClick={send} disabled={sending || !input.trim()} data-testid="mentor-send"
            className="w-11 h-11 rounded-full bg-[#4A5E2E] text-white grid place-items-center hover:opacity-90 disabled:opacity-50">
            <Send size={17} />
          </button>
        </div>
      </div>
    </div>
  );
}
